<template>
  <div :class="['BasemapPicker', { 'maplibregl-ctrl maplibregl-ctrl-group': !inline }]">
    <div
      v-if="offlineMode"
      class="BasemapPicker__item BasemapPicker__item--offline"
      basemap="offline"
      active="true"
      :title="t('myMap.basemapPicker.offlineTitle')"
    >
      <span class="BasemapPicker__badge">{{ t('myMap.basemapPicker.offline') }}</span>
    </div>
    <template v-else>
      <div
        class="BasemapPicker__item"
        v-for="b in basemaps"
        :key="b.id"
        :basemap="b.id"
        :active="b.id === basemap?.id"
        @click="basemap = b"
      ></div>
    </template>
  </div>
</template>

<script setup lang="ts">
defineProps<{ inline?: boolean }>()

import { useConnectivity } from '@/composables/useConnectivity'
import { DEFAULT_BASEMAP, ONLINE_BASEMAPS, isOfflineBasemapAvailable } from '@/config/basemaps'
import type Basemap from '@/models/interfaces/map/Basemap'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const { isOnline } = useConnectivity()

const basemap = defineModel<Basemap | null>({
  default: () => DEFAULT_BASEMAP,
})

const basemaps: Basemap[] = ONLINE_BASEMAPS

// Hors ligne, seul le fond local est disponible ; le choix en ligne est conservé et reprend
// dès le retour de la connexion.
const offlineMode = computed(() => !isOnline.value && isOfflineBasemapAvailable)
</script>

<style lang="scss">
.BasemapPicker {
  display: flex;
  flex-flow: row nowrap !important;
  gap: 0.5rem;

  .BasemapPicker__item {
    --dim-basemap-w: 3.25rem;
    width: var(--dim-basemap-w);
    height: var(--dim-basemap-w);
    background-size: cover;
    background-position: center;
    border-radius: 50%;
    border: solid 2px white;
    cursor: pointer;
    transition: all 0.15s ease-in;

    &[basemap='openstreetmap'],
    &[basemap='offline'] {
      background-image: url(@/assets/images/map/basemap/openstreetmap.jpg);
    }
    &[basemap='satellite'] {
      background-image: url(@/assets/images/map/basemap/satellite.jpg);
    }
    &[active='true'] {
      order: 1;
    }
    &:not([active='true']) {
      transform: scale(0);
      pointer-events: none;
      opacity: 0.5;
    }
  }

  .BasemapPicker__item--offline {
    position: relative;
    cursor: default;
  }

  .BasemapPicker__badge {
    position: absolute;
    left: 50%;
    bottom: -0.4rem;
    transform: translateX(-50%);
    padding: 0.05rem 0.35rem;
    border-radius: 0.5rem;
    background: #1a1a1a;
    color: white;
    font-size: 0.6rem;
    line-height: 1.2;
    white-space: nowrap;
  }

  &:hover {
    .BasemapPicker__item {
      transform: scale(1);
      pointer-events: all;

      &:hover {
        opacity: 1;
      }
    }
  }
}
</style>
